import { useQuery } from '@tanstack/react-query'
import { ApiError, getBearerToken } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import { isDemoSession } from '@/auth/oidc'
import { loginEmailFromUsername } from '@/api/map/create'
import { keys } from '@/api/hooks/shared/keys'
import { findDemoAgent } from './agentDemo'
import type { AgentMfaBatch, AgentMfaStatus } from './agentTypes'

function hasLiveAgentMfa(): boolean {
  if (isDemoSession()) return false
  // DEV Vite plugin; requires Keycloak login (Bearer).
  return Boolean(import.meta.env.DEV && getBearerToken())
}

async function fetchAgentMfaBatch(usernames: string[]): Promise<AgentMfaBatch> {
  const unique = [...new Set(usernames.map((u) => u.trim()).filter(Boolean))].slice(0, 40)
  if (unique.length === 0) return { users: [] }

  if (!hasLiveAgentMfa()) {
    return {
      users: unique.map((username) => {
        const demo = findDemoAgent(username) || findDemoAgent(loginEmailFromUsername(username))
        return (
          demo ?? {
            username,
            found: false,
            mfaEnabled: false,
            methods: [],
            keycloakUsername: null,
          }
        )
      }),
    }
  }

  const qs = new URLSearchParams({ usernames: unique.join(',') })
  const headers = new Headers()
  const bearer = getBearerToken()
  if (bearer) headers.set('Authorization', `Bearer ${bearer}`)

  let response: Response
  try {
    response = await fetch(`/api/agents/mfa-status?${qs}`, { headers })
  } catch {
    throw new ApiError(0, 'Keycloak MFA lookup unreachable', null)
  }
  const text = await response.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }
  if (!response.ok) {
    const message =
      typeof body === 'object' &&
      body &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : `Request failed (${response.status})`
    throw new ApiError(response.status, message, body)
  }
  return body as AgentMfaBatch
}

async function loadOne(username: string, accountId?: string): Promise<AgentMfaStatus | null> {
  const key = username.trim()
  if (!key && !accountId) return null
  try {
    const names = [key, key ? loginEmailFromUsername(key) : ''].filter(Boolean)
    const batch = names.length ? await fetchAgentMfaBatch(names) : { users: [] as AgentMfaStatus[] }
    const hit =
      batch.users.find((u) => u.found && u.mfaEnabled) ||
      batch.users.find((u) => u.found) ||
      batch.users[0] ||
      null
    if (hit?.found) return hit
    return (
      (accountId ? findDemoAgent(accountId) : null) ||
      (key ? findDemoAgent(key) : null) ||
      hit
    )
  } catch (err) {
    if (isUnreachableApiError(err) || (err instanceof ApiError && err.status === 502)) {
      return (accountId ? findDemoAgent(accountId) : null) || (key ? findDemoAgent(key) : null)
    }
    throw err
  }
}

export function useAgentMfaStatus(
  username: string | undefined,
  enabled = true,
  accountId?: string,
) {
  const key = username?.trim() || ''
  const id = accountId?.trim() || ''
  return useQuery({
    queryKey: keys.agentMfaUser(`${key}|${id}`),
    queryFn: () => loadOne(key, id || undefined),
    enabled: enabled && (key.length > 0 || id.length > 0),
    staleTime: 30_000,
  })
}

export function useAgentMfaBatch(usernames: string[], enabled = true) {
  const sorted = [...new Set(usernames.map((u) => u.trim()).filter(Boolean))].sort()
  return useQuery({
    queryKey: keys.agentMfaBatch(sorted),
    queryFn: () => fetchAgentMfaBatch(sorted),
    enabled: enabled && sorted.length > 0,
    staleTime: 20_000,
  })
}
